/**
 * Supplier Response Form - CR-1403
 *
 * Form for suppliers to submit their RFQ responses:
 * - NDA acceptance (if required)
 * - Line item pricing with quantity breaks
 * - Additional costs entry
 * - Certificate upload
 * - Notes and validity period
 */

import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Send,
  Save,
  Loader2,
  Plus,
  Trash2,
  FileText,
  Shield,
  AlertTriangle,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { rfqAPI } from '../../lib/api';
import type { RFQ, SupplierResponse, RFQCostType } from '../../lib/api';
import NDAAcceptance from './NDAAcceptance';

interface LineItemResponse {
  rfq_line_item: number;
  line_number: number;
  item_description: string;
  quantity: number;
  unit_of_measure: string;
  target_unit_price: number | null;
  unit_price: number;
  lead_time_days: number | null;
  notes: string;
}

interface AdditionalCostResponse {
  cost_type: RFQCostType;
  description: string;
  amount: number;
}

interface SupplierResponseFormProps {
  rfq: RFQ;
  response: SupplierResponse;
  onSuccess?: () => void;
  onClose?: () => void;
}

export default function SupplierResponseForm({
  rfq,
  response,
  onSuccess,
  onClose
}: SupplierResponseFormProps) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ndaAccepted, setNdaAccepted] = useState(response.nda_accepted);
  const [showNDA, setShowNDA] = useState(rfq.nda_required && !response.nda_accepted);

  // Response details
  const [validityDays, setValidityDays] = useState(response.validity_period_days || 30);
  const [leadTimeDays, setLeadTimeDays] = useState(response.lead_time_days || 14);
  const [paymentTerms, setPaymentTerms] = useState(response.payment_terms || '');
  const [deliveryTerms, setDeliveryTerms] = useState(response.delivery_terms || '');
  const [notes, setNotes] = useState(response.notes || '');

  // Line item responses
  const [lineResponses, setLineResponses] = useState<LineItemResponse[]>([]);

  // Additional costs
  const [additionalCosts, setAdditionalCosts] = useState<AdditionalCostResponse[]>([]);

  useEffect(() => {
    // Initialize line responses from RFQ line items
    if (rfq.line_items) {
      const initialResponses = rfq.line_items.map(item => {
        // Check if there's an existing response for this line
        const existingLine = response.line_items?.find(
          l => l.rfq_line_item === item.id
        );
        return {
          rfq_line_item: item.id,
          line_number: item.line_number,
          item_description: item.item_description,
          quantity: item.quantity,
          unit_of_measure: item.unit_of_measure,
          target_unit_price: item.target_unit_price,
          unit_price: existingLine?.unit_price || 0,
          lead_time_days: existingLine?.lead_time_days || null,
          notes: existingLine?.notes || '',
        };
      });
      setLineResponses(initialResponses);
    }

    // Initialize additional costs from existing response
    if (response.additional_costs) {
      setAdditionalCosts(
        response.additional_costs.map(cost => ({
          cost_type: cost.cost_type,
          description: cost.description,
          amount: cost.amount,
        }))
      );
    }
  }, [rfq, response]);

  const handleNDAAccept = async () => {
    try {
      setLoading(true);
      await rfqAPI.acceptNDA(response.id);
      setNdaAccepted(true);
      setShowNDA(false);
      toast.success('NDA accepted successfully');
    } catch (error) {
      console.error('Error accepting NDA:', error);
      toast.error('Failed to accept NDA');
    } finally {
      setLoading(false);
    }
  };

  const handleNDADecline = async () => {
    try {
      setLoading(true);
      await rfqAPI.declineNDA(response.id);
      toast.info('NDA declined. You cannot respond to this RFQ.');
      onClose?.();
    } catch (error) {
      console.error('Error declining NDA:', error);
      toast.error('Failed to decline NDA');
    } finally {
      setLoading(false);
    }
  };

  const updateLineResponse = (index: number, field: keyof LineItemResponse, value: number | string | null) => {
    const updated = [...lineResponses];
    updated[index] = { ...updated[index], [field]: value };
    setLineResponses(updated);
  };

  const addAdditionalCost = () => {
    setAdditionalCosts([
      ...additionalCosts,
      { cost_type: 'tooling', description: '', amount: 0 }
    ]);
  };

  const removeAdditionalCost = (index: number) => {
    setAdditionalCosts(additionalCosts.filter((_, i) => i !== index));
  };

  const updateAdditionalCost = (index: number, field: keyof AdditionalCostResponse, value: string | number) => {
    const updated = [...additionalCosts];
    updated[index] = { ...updated[index], [field]: value };
    setAdditionalCosts(updated);
  };

  const calculateTotals = () => {
    const lineItemsTotal = lineResponses.reduce((sum, item) => {
      return sum + (item.unit_price * item.quantity);
    }, 0);
    const costsTotal = additionalCosts.reduce((sum, cost) => sum + cost.amount, 0);
    return {
      lineItemsTotal,
      costsTotal,
      grandTotal: lineItemsTotal + costsTotal
    };
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Update response details
      await rfqAPI.updateResponse(response.id, {
        validity_period_days: validityDays,
        lead_time_days: leadTimeDays,
        payment_terms: paymentTerms,
        delivery_terms: deliveryTerms,
        notes: notes,
      });

      // Update/create line item responses
      for (const lineResp of lineResponses) {
        const existingLine = response.line_items?.find(
          l => l.rfq_line_item === lineResp.rfq_line_item
        );

        if (existingLine) {
          await rfqAPI.updateResponseLineItem(existingLine.id, {
            unit_price: lineResp.unit_price,
            lead_time_days: lineResp.lead_time_days || undefined,
            notes: lineResp.notes,
          });
        } else {
          await rfqAPI.createResponseLineItem({
            response: response.id,
            rfq_line_item: lineResp.rfq_line_item,
            unit_price: lineResp.unit_price,
            lead_time_days: lineResp.lead_time_days || undefined,
            notes: lineResp.notes,
          });
        }
      }

      // Handle additional costs
      // Delete existing and recreate (simpler than tracking changes)
      if (response.additional_costs) {
        for (const cost of response.additional_costs) {
          await rfqAPI.deleteResponseAdditionalCost(cost.id);
        }
      }

      for (const cost of additionalCosts) {
        await rfqAPI.createResponseAdditionalCost({
          response: response.id,
          cost_type: cost.cost_type,
          description: cost.description,
          amount: cost.amount,
          currency: rfq.currency,
        });
      }

      toast.success('Response saved');
      onSuccess?.();
    } catch (error) {
      console.error('Error saving response:', error);
      toast.error('Failed to save response');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all line items have prices
    const missingPrices = lineResponses.filter(l => l.unit_price <= 0);
    if (missingPrices.length > 0) {
      toast.error('Please provide prices for all line items');
      return;
    }

    try {
      setSubmitting(true);

      // Save first
      await handleSave();

      // Then submit
      await rfqAPI.submitResponse(response.id);
      toast.success('Response submitted successfully');
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Error submitting response:', error);
      toast.error('Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  // Show NDA acceptance if required
  if (showNDA) {
    return (
      <NDAAcceptance
        onAccept={handleNDAAccept}
        onDecline={handleNDADecline}
        loading={loading}
      />
    );
  }

  const totals = calculateTotals();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Submit Response</h2>
          <p className="text-muted-foreground">RFQ: {rfq.rfq_number} - {rfq.title}</p>
        </div>
        {ndaAccepted && (
          <Badge variant="outline" className="text-green-600 border-green-600">
            <Check className="h-3 w-3 mr-1" /> NDA Accepted
          </Badge>
        )}
      </div>

      {/* Response Status */}
      {response.response_status !== 'pending' && response.response_status !== 'in_progress' && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <span className="text-sm">
              This response has been {response.response_status}. Editing is disabled.
            </span>
          </CardContent>
        </Card>
      )}

      {/* Line Item Pricing */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Line Item Pricing</CardTitle>
          <CardDescription>Enter your unit prices for each item</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {lineResponses.map((item, index) => (
              <div key={item.rfq_line_item} className="grid grid-cols-6 gap-3 p-3 border rounded-lg">
                <div className="col-span-2">
                  <Label className="text-xs text-muted-foreground">Item {item.line_number}</Label>
                  <p className="font-medium text-sm">{item.item_description}</p>
                  <p className="text-xs text-muted-foreground">
                    Qty: {item.quantity.toLocaleString()} {item.unit_of_measure}
                  </p>
                  {item.target_unit_price && (
                    <p className="text-xs text-muted-foreground">
                      Target: {rfq.currency} {item.target_unit_price.toFixed(2)}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Unit Price ({rfq.currency}) *</Label>
                  <Input
                    type="number"
                    value={item.unit_price || ''}
                    onChange={(e) => updateLineResponse(index, 'unit_price', parseFloat(e.target.value) || 0)}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label className="text-xs">Lead Time (days)</Label>
                  <Input
                    type="number"
                    value={item.lead_time_days || ''}
                    onChange={(e) => updateLineResponse(index, 'lead_time_days', e.target.value ? parseInt(e.target.value) : null)}
                    min="0"
                    placeholder="14"
                  />
                </div>
                <div>
                  <Label className="text-xs">Line Total</Label>
                  <div className="h-9 flex items-center font-medium">
                    {rfq.currency} {(item.unit_price * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Input
                    value={item.notes}
                    onChange={(e) => updateLineResponse(index, 'notes', e.target.value)}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Additional Costs */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-lg">Additional Costs</CardTitle>
              <CardDescription>Tooling, packaging, transport, etc.</CardDescription>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addAdditionalCost}>
              <Plus className="h-4 w-4 mr-1" /> Add Cost
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {additionalCosts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No additional costs. Click "Add Cost" if applicable.
            </p>
          ) : (
            <div className="space-y-3">
              {additionalCosts.map((cost, index) => (
                <div key={index} className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Type</Label>
                    <select
                      value={cost.cost_type}
                      onChange={(e) => updateAdditionalCost(index, 'cost_type', e.target.value)}
                      className="w-full p-2 border rounded-md text-sm"
                    >
                      <option value="tooling">Tooling</option>
                      <option value="packaging">Packaging</option>
                      <option value="transport">Transport</option>
                      <option value="setup">Setup</option>
                      <option value="certification">Certification</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={cost.description}
                      onChange={(e) => updateAdditionalCost(index, 'description', e.target.value)}
                      placeholder="Description"
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Amount ({rfq.currency})</Label>
                      <Input
                        type="number"
                        value={cost.amount || ''}
                        onChange={(e) => updateAdditionalCost(index, 'amount', parseFloat(e.target.value) || 0)}
                        step="0.01"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-5"
                      onClick={() => removeAdditionalCost(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Required Certificates */}
      {rfq.required_certificates && rfq.required_certificates.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Required Certificates
            </CardTitle>
            <CardDescription>Upload required certificates before submission</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rfq.required_certificates.map((cert) => (
                <div key={cert.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{cert.certificate_name}</span>
                      <Badge variant={cert.is_mandatory ? 'default' : 'secondary'} className="text-xs">
                        {cert.is_mandatory ? 'Required' : 'Optional'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{cert.description}</p>
                  </div>
                  <Button variant="outline" size="sm">
                    <FileText className="h-4 w-4 mr-1" />
                    Upload
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Response Details */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Response Details</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <Label>Quote Validity (days)</Label>
            <Input
              type="number"
              value={validityDays}
              onChange={(e) => setValidityDays(parseInt(e.target.value) || 30)}
              min={1}
            />
          </div>
          <div>
            <Label>Overall Lead Time (days)</Label>
            <Input
              type="number"
              value={leadTimeDays}
              onChange={(e) => setLeadTimeDays(parseInt(e.target.value) || 14)}
              min={0}
            />
          </div>
          <div>
            <Label>Payment Terms</Label>
            <Input
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              placeholder="e.g., Net 30 days"
            />
          </div>
          <div>
            <Label>Delivery Terms</Label>
            <Input
              value={deliveryTerms}
              onChange={(e) => setDeliveryTerms(e.target.value)}
              placeholder="e.g., DDP, EXW"
            />
          </div>
          <div className="col-span-2">
            <Label>Additional Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional information or conditions..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Totals */}
      <Card className="bg-muted/50">
        <CardContent className="pt-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span>Line Items Total:</span>
            <span>{rfq.currency} {totals.lineItemsTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Additional Costs:</span>
            <span>{rfq.currency} {totals.costsTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-lg font-bold pt-2 border-t">
            <span>Grand Total:</span>
            <span>{rfq.currency} {totals.grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Save className="h-4 w-4 mr-2" />
            Save Draft
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || response.response_status === 'submitted'}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Send className="h-4 w-4 mr-2" />
            Submit Response
          </Button>
        </div>
      </div>
    </div>
  );
}
